import React, { useMemo, useState } from 'react'
import { currentAdminCan } from '../data/currentAdminAccess'
import AdminActionPermissionEnhancer from './AdminActionPermissionEnhancer'
import './AdminLogout.css'

const nav = [
  { label: '대시보드', path: '/dashboard', icon: '◫' },
  { label: '회원', icon: '◎', children: [
    { label: '전체 회원', path: '/members' },
    { label: '디렉터 신청 관리', path: '/director-reviews' }
  ] },
  { label: '견적·주문', icon: '▤', children: [
    { label: '견적 요청', path: '/quote-requests' },
    { label: '디렉터 제안 견적', path: '/quotes' },
    { label: '주문·진행 관리', path: '/orders' }
  ] },
  { label: '결제·정산', icon: '₩', children: [
    { label: '결제', path: '/payments' },
    { label: '환불·분쟁 관리', path: '/refunds' },
    { label: '정산', path: '/settlements' }
  ] },
  { label: '콘텐츠', icon: '▣', children: [
    { label: '메인 화면 편집', path: '/content/home' },
    { label: '브랜드·로고·OG 설정', path: '/content/branding' },
    { label: '공지사항 관리', path: '/content/notices' },
    { label: '디팬 이야기 관리', path: '/content/stories' },
    { label: '이벤트 관리', path: '/content/events' },
    { label: '메인 배너 관리', path: '/content/banners' },
    { label: '정책·안내 페이지', path: '/content/info-pages' },
    { label: '디렉터 포트폴리오 조회', path: '/content/portfolios' },
    { label: '이용자 후기·신고', path: '/content/reviews' },
    { label: '블라인드·삭제 관리', path: '/content/moderation' }
  ] },
  { label: 'AI', icon: '✦', children: [
    { label: 'AI 운영 현황', path: '/ai' },
    { label: '견적 분석', path: '/ai/quote-analysis' },
    { label: '디렉터 큐레이션', path: '/ai/curation' },
    { label: '추천 가이드라인', path: '/ai/guidelines' },
    { label: 'AI 시안 생성', path: '/ai/mockups' },
    { label: 'AI 실행 이력', path: '/ai/runs' },
    { label: '모델·비용 관리', path: '/ai/models' }
  ] },
  { label: '고객지원', icon: '◌', children: [
    { label: '문의 관리', path: '/support/inquiries' },
    { label: '알림 설정·발송', path: '/support/notifications' }
  ] },
  { label: '운영 설정', icon: '⚙', children: [
    { label: '서비스 관리', path: '/settings/services' },
    { label: '견적 질문 관리', path: '/settings/questions' },
    { label: '서비스 분류', path: '/settings/categories' },
    { label: '지역 관리', path: '/settings/regions' },
    { label: '태그 관리', path: '/settings/tags' }
  ] },
  { label: '시스템', icon: '⊙', children: [
    { label: '관리자 계정·권한', path: '/system/admins' },
    { label: '감사 로그', path: '/system/audit-logs' }
  ] }
]

function isActive(current, target) {
  const path = target.split('?')[0]
  if (path === '/dashboard' || path === '/ai') return current === path
  if (path === '/orders') return current === path || current.startsWith('/orders/') || current.startsWith('/projects')
  return current === path || current.startsWith(`${path}/`)
}

function visibleNavigation() {
  return nav.map((item) => {
    if (item.path) return currentAdminCan(item.path, 'view') ? item : null
    const children = (item.children || []).filter((child) => currentAdminCan(child.path, 'view'))
    return children.length ? { ...item, children } : null
  }).filter(Boolean)
}

export default function AdminLayout({ route, navigate, admin, onLogout, children }) {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [openGroups, setOpenGroups] = useState({})
  const currentAdmin = admin || { email: '관리자', role: 'NONE' }
  const visibleNav = useMemo(() => visibleNavigation(), [route, currentAdmin.role])
  const activeGroup = useMemo(() => visibleNav.find((item) => item.path ? isActive(route, item.path) : item.children?.some((child) => isActive(route, child.path))), [route, visibleNav])
  const go = (path) => { setMobileOpen(false); navigate(path) }
  const toggleGroup = (label) => setOpenGroups((current) => ({ ...current, [label]: !current[label] }))
  const canSeeNotifications = currentAdminCan('/support/notifications', 'view')
  const displayName = currentAdmin.name || currentAdmin.email?.split('@')[0] || '관리자'
  const logout = () => { setMobileOpen(false); onLogout?.() }

  return <div className="admin-shell">
    <aside className={`admin-sidebar${mobileOpen ? ' is-open' : ''}`}>
      <div className="admin-brand" onClick={() => go('/dashboard')} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') go('/dashboard') }} role="button" tabIndex={0}><div className="admin-brand-mark">D</div><div><strong>difan</strong><span>ADMIN</span></div></div>
      <nav className="admin-nav" aria-label="관리자 메뉴">{visibleNav.map((item) => {
        if (item.path) return <div className="admin-nav-group" key={item.label}><button className={isActive(route, item.path) ? 'active' : ''} onClick={() => go(item.path)}><span className="nav-icon">{item.icon}</span><span>{item.label}</span></button></div>
        const expanded = Boolean(openGroups[item.label])
        const hasActive = item.children?.some((child) => isActive(route, child.path))
        return <div className={`admin-nav-group${expanded ? ' is-expanded' : ''}`} key={item.label}>
          <button type="button" className={`nav-group-toggle${hasActive ? ' has-active' : ''}`} aria-expanded={expanded} onClick={() => toggleGroup(item.label)}>
            <span className="nav-icon">{item.icon}</span><span>{item.label}</span><span className="nav-group-chevron" aria-hidden="true">⌄</span>
          </button>
          {expanded ? <div className="nav-submenu">{item.children.map((child) => <button type="button" key={child.path} className={isActive(route, child.path) ? 'active' : ''} onClick={() => go(child.path)}>{child.label}</button>)}</div> : null}
        </div>
      })}</nav>
      <div className="admin-sidebar-footer"><span>DATABASE</span><strong>difan_dev · LIVE API</strong><small>NCP PostgreSQL · Object Storage</small><button type="button" className="admin-sidebar-logout" onClick={logout}>로그아웃</button></div>
    </aside>
    {mobileOpen ? <button className="sidebar-backdrop" aria-label="메뉴 닫기" onClick={() => setMobileOpen(false)} /> : null}
    <div className="admin-main">
      <header className="admin-topbar"><div className="topbar-left"><button className="mobile-menu-button" onClick={() => setMobileOpen(true)}>☰</button><div className="topbar-context"><span>운영 관리자</span><strong>{activeGroup?.label || 'DIFAN ADMIN'}</strong></div></div><div className="topbar-right">{canSeeNotifications ? <button className="topbar-icon" title="알림 관리" onClick={() => go('/support/notifications')}>♢<i /></button> : null}<div className="admin-user"><span>{displayName.slice(0, 1).toUpperCase()}</span><div><strong>{displayName}</strong><small>{currentAdmin.role || '관리자'}</small></div><button type="button" className="admin-logout-button" onClick={logout}>로그아웃</button></div></div></header>
      <main className="admin-content"><AdminActionPermissionEnhancer route={route} />{children}</main>
    </div>
  </div>
}
